import { BodyContainer, Cell, Footer, Header, Row, Table } from "wed-components-beta";
import { BillingsWithCashFlow, CashFlowInstallment } from "./CashFlow";
import { Alert, IconButton, InputAdornment, MenuItem, TextField, Tooltip } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { allowMoneyFormat, formatNumberToMoney } from "../../../Utils";
import { useLookupTables } from "../../../../hooks/useLookupTables";
import PreLoader from "../../../PreLoader";
import { Check, Delete } from "@mui/icons-material";
import { DateTime } from "luxon";

type Props = {
  form: BillingsWithCashFlow
  updateCashFlowInstallmentByIndex: (index: number, updates: Partial<CashFlowInstallment>) => void
  deleteInstallment: (index: number, id: number | undefined) => void
}

export default function InstallmentsTable({ form, updateCashFlowInstallmentByIndex, deleteInstallment }: Props) {
  // Hooks
  const { lookupTables, errorLookupTables, isLoadingLookupTables } = useLookupTables(['paymentMethods'])
  // Constants
  const columnWidth = [.75, 1.5, 1.25, 1.25, 1.75, 3, .5]

  // Convenience
  const handleNavigate = (code: string, elementId: string, row: number) => {
    switch (code) {
      case 'ArrowUp':
        return document.getElementById(`${elementId}${row - 1}`)?.focus()
      case 'ArrowDown':
        return document.getElementById(`${elementId}${row + 1}`)?.focus()
      // Ignore any button other than the arrows
      default:
        return
    }
  }

  if (isLoadingLookupTables) return <PreLoader />

  if (errorLookupTables || !lookupTables) return <Alert severity="error">Could not load lookup tables.</Alert>

  return <Table id='table-cash-flow'>
    <BodyContainer flexGrow={1}>
      <Header>
        <Cell width={columnWidth[0]}>
          ID
        </Cell>
        <Cell width={columnWidth[1]}>
          Due Date
        </Cell>
        <Cell width={columnWidth[2]}>
          Gross Value
        </Cell>
        <Cell width={columnWidth[3]}>
          Due Value
        </Cell>
        <Cell width={columnWidth[4]}>
          Payment Method
        </Cell>
        <Cell flexGrow>
          Comments
        </Cell>
        <Cell width={columnWidth[6]}>
          Paid?
        </Cell>
        <Cell width={columnWidth[6]}>
          Delete
        </Cell>
      </Header>
      {
        !form.cashFlowInstallments.length ?
          <Row>
            <Cell flexGrow>
              No installments to show.
            </Cell>
          </Row>
          :
          form.cashFlowInstallments.map((installment, index) => <Row key={index}>
            <Cell width={columnWidth[0]}>
              <TextField
                id={`cash-flow-id-${index}`}
                label='ID'
                value={installment.id || ''}
                onChange={console.log}
                disabled={!installment.id}
              />
            </Cell>
            <Cell width={columnWidth[1]}>
              <LocalizationProvider dateAdapter={AdapterLuxon}>
                <DatePicker
                  slotProps={{
                    textField: {
                      id: `cash-flow-due-date-${index}`
                    }
                  }}
                  format="dd-MMM-yy"
                  label="Due date"
                  value={installment.due_date}
                  onChange={(newValue) => updateCashFlowInstallmentByIndex(index, { due_date: newValue })}
                />
              </LocalizationProvider>
            </Cell>
            <Cell width={columnWidth[2]}>
              <TextField
                id={`cash-flow-gross-value-${index}`}
                label='Gross value'
                value={installment.grossValue.label}
                onChange={(e) => updateCashFlowInstallmentByIndex(index, { grossValue: { value: allowMoneyFormat(e.target.value), label: e.target.value } })}
                error={isNaN(installment.grossValue.value)}
                onKeyDown={(e) => handleNavigate(e.code, 'cash-flow-gross-value-', index)}
                onBlur={() => {
                  const _ = { ...installment }

                  _.grossValue.label = formatNumberToMoney(_.grossValue.value)

                  updateCashFlowInstallmentByIndex(index, _)
                }}
                onFocus={() => {
                  const _ = { ...installment }

                  _.grossValue.label = _.grossValue.value.toFixed(2)

                  updateCashFlowInstallmentByIndex(index, _)
                }}
                InputProps={
                  { startAdornment: <InputAdornment position='start'>{form.billing.currency.sign}</InputAdornment> }
                }
              />
            </Cell>
            <Cell width={columnWidth[3]}>
              <TextField
                id={`cash-flow-due-value-${index}`}
                label='Due value'
                value={installment.dueValue.label}
                onChange={(e) => updateCashFlowInstallmentByIndex(index, { dueValue: { value: allowMoneyFormat(e.target.value), label: e.target.value } })}
                error={isNaN(installment.dueValue.value)}
                onKeyDown={(e) => handleNavigate(e.code, 'cash-flow-due-value-', index)}
                onBlur={() => {
                  const _ = { ...installment }

                  _.dueValue.label = formatNumberToMoney(_.dueValue.value)

                  updateCashFlowInstallmentByIndex(index, _)
                }}
                onFocus={() => {
                  const _ = { ...installment }

                  _.dueValue.label = _.dueValue.value.toFixed(2)

                  updateCashFlowInstallmentByIndex(index, _)

                }}
                InputProps={
                  { startAdornment: <InputAdornment position='start'>{form.billing.currency.sign}</InputAdornment> }
                }
              />
            </Cell>
            <Cell width={columnWidth[4]}>
              <TextField
                id={`cash-flow-payment-method-${index}`}
                label='Payment method'
                value={installment.fk_id_payment_method}
                onChange={(e) => updateCashFlowInstallmentByIndex(index, { fk_id_payment_method: Number(e.target.value) })}
                onKeyDown={(e) => handleNavigate(e.code, 'cash-flow-payment-method-', index)}
                select
              >
                {
                  lookupTables.paymentMethods.map((_, index) => <MenuItem key={index} id={`payment-method-option-${_.id}`} value={_.id}>{_.label}</MenuItem>)
                }
              </TextField>
            </Cell>
            <Cell flexGrow>
              <TextField
                id={`cash-flow-comments-${index}`}
                label='Comments'
                value={installment.comment || ''}
                onChange={(e) => updateCashFlowInstallmentByIndex(index, { comment: e.target.value })}
                onKeyDown={(e) => handleNavigate(e.code, 'cash-flow-comments-', index)}
              />
            </Cell>
            <Cell width={columnWidth[6]}>
              <Tooltip title='Mark as paid'>
                <IconButton
                  id={`button-mark-as-paid-${index}`}
                  onClick={(e) => updateCashFlowInstallmentByIndex(index, { paid_date: installment.paid_date || DateTime.now(), paid_value: installment.dueValue.value })}
                  sx={{ backgroundColor: installment.paid_date ? 'var(--color-green)' : '', width: 36, height: 36 }}
                >
                  <Check />
                </IconButton>
              </Tooltip>
            </Cell>
            <Cell width={columnWidth[6]}>
              <Tooltip title='Delete installment'>
                <IconButton
                  id={`button-delete-cash-flow-${index}`}
                  color='error'
                  onClick={() => deleteInstallment(index, installment.id)}
                  sx={{ width: 36, height: 36 }}

                >
                  <Delete />
                </IconButton>
              </Tooltip>
            </Cell>
          </Row>)
      }
      <Footer>
        <Cell flexGrow>
          Total: {form.cashFlowInstallments.length} installments
        </Cell>
      </Footer>
    </BodyContainer>
  </Table>
}
